/**
 * 주렁맨(`pages/ZoolungmanPage.jsx`) 순수 로직 — **렌더와 분리**.
 * ============================================================
 * 설계: `docs/2026-07-07-zoolungman-coin-grant-app-dev-steps.md` §S4
 *      / 기획 `docs/2026-07-07-zoolungman-coin-grant-app.md` (RF1~RF3, D2·D8·D9)
 *
 * ⚠️ 왜 분리하는가 — 이 저장소 `tools` 에는 `@testing-library/react` 가 **설치되어 있지 않아**
 *    컴포넌트 렌더 테스트가 불가능합니다(`ls tools/node_modules/@testing-library` → 없음).
 *    판단이 들어가는 로직(잔액 정규화·수량 검증·에러 매핑·중복 스캔 억제)을 여기에 두면
 *    JSX 없이도 jest 로 **전부 검증**할 수 있고, 페이지는 조립만 담당하게 됩니다.
 *
 * 이 모듈은 React·CSS·axios 를 **import 하지 않습니다**(순수 함수만).
 */

/** 1회 지급 최소 수량. */
export const GIVE_MIN_AMOUNT = 1;

/**
 * 1회 지급 최대 수량(D2 — 스탭 재량 상한).
 * 🔴 서버 `api-server/src/config/settings.py:198` 의 `ZOOLUNGMAN_MAX_AMOUNT`(=30) 과 **같은 값이어야** 합니다.
 *    서버가 Pydantic `le=` 로 선차단(422)하므로 FE 값이 더 크면 "정상 입력인데 422" 가 나고,
 *    더 작으면 서버가 허용하는 수량을 FE 가 막습니다. 한쪽을 바꾸면 반드시 양쪽을 바꾸십시오.
 * ⚠️ 이것은 "적립 정책 금액"이 아니라 재량 상한이므로 reward_settings 조회 대상이 아닙니다
 *    (CLAUDE.md 주의 8 / settings.py:196-198 주석과 동일 근거).
 */
export const GIVE_MAX_AMOUNT = 30;

/** 빠른 선택 버튼 수량(RF2 — `[1] [3] [직접입력]`). */
export const QUICK_AMOUNTS = Object.freeze([1, 3]);

/**
 * 같은 QR 재조회 쿨다운(ms).
 * `QRScanner` 는 디코드 **프레임마다** `onScan` 을 부르므로(`QRScanner.js:88-101`), 조회 실패한 QR 이
 * 카메라 앞에 계속 놓여 있으면 초당 여러 번 API 를 때립니다. 같은 코드는 이 시간 동안 재조회하지 않습니다.
 */
export const SAME_CODE_COOLDOWN_MS = 3000;

/**
 * 잔액을 숫자로 정규화합니다 — **D8 핵심**.
 *
 * 🔴 서버 `_format_member` 의 `row.get("balance", 0)`(`member_service.py:457`)은
 *    LEFT JOIN 결과가 NULL 이면 **키가 존재하므로 기본값 0 이 적용되지 않고 `None`** 을 돌려줍니다.
 *    즉 응답 JSON 에 `"balance": null` 이 실제로 옵니다(잔액행 부재 40명). 그대로 렌더하면
 *    화면에 빈칸이 찍히고 `balance + amount` 는 `NaN` 이 됩니다.
 *
 * @param {*} balance 서버가 준 잔액(숫자 | null | undefined | 숫자문자열)
 * @returns {number} 항상 유한한 숫자
 */
export const normalizeBalance = (balance) => {
  const parsed = Number(balance ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * 회원 표시명 — 별명 우선, 없으면 이름, 그것도 없으면 회원 ID.
 * (회원 상당수가 `name` 미입력이라 별명이 현장에서 유일한 식별 문구입니다.)
 *
 * @param {object} member `getMemberByQR` 응답의 `member` 객체
 * @returns {string} 항상 비어있지 않은 문자열
 */
export const memberDisplayName = (member) => {
  if (!member) return '회원';
  const nickname = typeof member.nickname === 'string' ? member.nickname.trim() : '';
  if (nickname) return nickname;
  const name = typeof member.name === 'string' ? member.name.trim() : '';
  if (name) return name;
  const memberId = typeof member.member_id === 'string' ? member.member_id.trim() : '';
  return memberId || '회원';
};

/**
 * `getMemberByQR` 응답 → 화면이 쓰는 회원 카드로 축약합니다.
 * 응답 형태: `{ success, member: {...}, coin: { balance } }` (`api-server/src/routers/member.py:100-119`)
 * — axios 응답 인터셉터(`services/api.js:74-77`)가 `response.data` 를 그대로 돌려주므로
 *   `res.data.member` 가 아니라 `res.member` 입니다.
 *
 * @param {object} res `getMemberByQR` 이 resolve 한 값
 * @returns {{memberId:string, displayName:string, balance:number, status:string|null}|null}
 *          회원 식별 불가 시 `null`(호출부가 무음 실패 대신 안내를 띄우도록)
 */
export const toMemberCard = (res) => {
  const member = res?.member;
  const memberId = member?.member_id;
  if (typeof memberId !== 'string' || !memberId) return null;
  return {
    memberId,
    displayName: memberDisplayName(member),
    // ⚠️ `res.coin.balance` 가 null 일 수 있음(D8) → normalizeBalance 필수.
    balance: normalizeBalance(res?.coin?.balance),
    status: typeof member.status === 'string' ? member.status : null,
  };
};

/**
 * 확인 모달의 "지급 후 잔액" 미리보기.
 * @param {*} balance 현재 잔액(null 가능 — D8)
 * @param {*} amount 지급 수량
 * @returns {number}
 */
export const previewBalanceAfter = (balance, amount) =>
  normalizeBalance(balance) + normalizeBalance(amount);

/** 직접입력 검증 실패 문구 — 화면이 아니라 여기서 결정합니다(문구 중복 방지). */
export const AMOUNT_ERROR_EMPTY = '지급할 수량을 입력해 주세요';
export const AMOUNT_ERROR_NOT_INTEGER = '수량은 정수만 입력할 수 있습니다';
export const AMOUNT_ERROR_RANGE = `수량은 ${GIVE_MIN_AMOUNT}~${GIVE_MAX_AMOUNT} 사이여야 합니다`;

/**
 * 직접입력 수량을 검증합니다(RF2 모달).
 *
 * 🔴 서버 `GiveCoinRequest.amount` 는 **`strict=True`** 라 문자열 `"3"` 을 보내면 **422** 입니다
 *    (`api-server/src/routers/tools_coin.py` `GiveCoinRequest`). `<input type="number">` 의
 *    `e.target.value` 는 문자열이므로 여기서 **숫자로 변환한 값(`amount`)만** 호출부로 넘깁니다.
 *
 * @param {*} raw 입력 원문(문자열 또는 숫자)
 * @returns {{ok:true, amount:number}|{ok:false, message:string}}
 */
export const validateGiveAmount = (raw) => {
  const text = typeof raw === 'string' ? raw.trim() : raw;
  if (text === '' || text === null || text === undefined) {
    return { ok: false, message: AMOUNT_ERROR_EMPTY };
  }
  const parsed = Number(text);
  if (!Number.isFinite(parsed)) return { ok: false, message: AMOUNT_ERROR_EMPTY };
  // 3.5 주화 같은 값은 서버 strict int 가 422 로 거부하므로 FE 에서 먼저 막습니다.
  if (!Number.isInteger(parsed)) return { ok: false, message: AMOUNT_ERROR_NOT_INTEGER };
  if (parsed < GIVE_MIN_AMOUNT || parsed > GIVE_MAX_AMOUNT) {
    return { ok: false, message: AMOUNT_ERROR_RANGE };
  }
  return { ok: true, amount: parsed };
};

/**
 * 지급(`POST /tools/coin/give`) 실패 문구 — **스캔 실패와 별개**(dev-steps S4 작업 8 / P9).
 * `constants/scanErrorMessages.js` 는 "회원 조회" 실패 전용이라 지급 실패를 덮지 못합니다.
 *
 * HTTP status 로 가릅니다(서버 error_code 가 아니라):
 *  - 422 `VALIDATION_ERROR` (Pydantic `gt=0 le=30` / strict int) — `middleware/error_handler.py:160-166`
 *  - 400 `ValidationException` (비 ACTIVE 회원) — `routers/tools_coin.py` 단계 2)
 *  - 404 `MemberNotFoundException` — `member_service.get_member_by_id`
 *  - 403 `ForbiddenException` (STAFF 미만) — `middleware/jwt_auth.py:35`
 */
export const GIVE_ERROR_MESSAGES = Object.freeze({
  422: AMOUNT_ERROR_RANGE,
  400: '지급할 수 없는 회원입니다',
  404: '회원을 찾을 수 없습니다',
  403: '지급 권한이 없습니다 (STAFF 이상)',
});

/** 위 4종에 해당하지 않는 모든 실패(500·네트워크 단절·타임아웃 포함). */
export const GIVE_ERROR_DEFAULT = '지급에 실패했습니다. 다시 시도해 주세요';

/**
 * 실패 객체에서 HTTP status 를 뽑습니다.
 *
 * ⚠️ **이 저장소의 실제 형태에 주의**: `services/api.js` 응답 인터셉터(`api.js:110-129`)가
 *    에러를 `{success:false, message, error_code, status}` **평면 객체로 정규화해 reject** 합니다.
 *    따라서 `err.response.data...` 가 아니라 `err.status` / `err.error_code` 가 실제 경로입니다.
 *    (dev-steps S4 는 `err.response.data.error_code` 로 적혀 있으나 이 코드베이스에서는 성립하지 않음.)
 *    다만 인터셉터를 거치지 않은 raw axios 에러가 흘러들 여지를 남겨 **양쪽 다** 봅니다.
 *
 * @param {*} err reject 된 값
 * @returns {number|null}
 */
export const giveErrorStatus = (err) => {
  const status = err?.status ?? err?.response?.status;
  return typeof status === 'number' ? status : null;
};

/**
 * 지급 실패 → 스탭에게 보여줄 한글 안내. 어떤 입력에도 **빈 문자열을 돌려주지 않습니다**.
 * @param {*} err reject 된 값
 * @returns {string}
 */
export const giveErrorMessage = (err) => {
  const status = giveErrorStatus(err);
  if (status === null) return GIVE_ERROR_DEFAULT;
  // ⚠️ 숫자 키라도 프로토타입 오염 방어를 위해 자기 키만 인정합니다(scanErrorMessage 와 동일 관례).
  if (!Object.prototype.hasOwnProperty.call(GIVE_ERROR_MESSAGES, status)) {
    return GIVE_ERROR_DEFAULT;
  }
  return GIVE_ERROR_MESSAGES[status];
};

/**
 * 파싱 실패 스캔(`onInvalidScan`)의 **중복 억제** — D9.
 *
 * 🔴 `QRScanner` 는 디코드 성공 프레임마다 콜백을 부릅니다(`QRScanner.js:88-101`). 구형 밴드를
 *    카메라 앞에 대고 있으면 초당 수 회 같은 원문이 들어오므로, 그대로 setState 하면 렌더 폭주 +
 *    안내가 깜빡입니다. **직전 원문과 같으면 무시**합니다.
 *
 * 순수 함수로 둔 이유: "상태 갱신 여부" 판정이 곧 테스트 대상이기 때문입니다.
 *
 * @param {string|null} prevRaw 직전에 처리한 스캔 원문
 * @param {*} rawValue 이번 스캔 원문
 * @param {(raw:string)=>string} messageFor 원문 → 안내 문구 변환기(`invalidScanMessage` 주입)
 * @returns {{changed:boolean, lastRaw:string|null, message:string|null}}
 */
export const nextInvalidScanState = (prevRaw, rawValue, messageFor) => {
  const unchanged = { changed: false, lastRaw: prevRaw ?? null, message: null };
  if (typeof rawValue !== 'string' || !rawValue) return unchanged;
  if (rawValue === prevRaw) return unchanged;
  return {
    changed: true,
    lastRaw: rawValue,
    message: typeof messageFor === 'function' ? messageFor(rawValue) : null,
  };
};

/**
 * 같은 QR 을 쿨다운 안에 다시 조회하려는지 판정합니다.
 * @param {{code:string, at:number}|null} lastScan 직전 조회 기록
 * @param {string} code 이번 스캔 코드
 * @param {number} now `Date.now()`
 * @param {number} cooldownMs 쿨다운(ms)
 * @returns {boolean} true 면 이번 스캔을 **건너뜁니다**
 */
export const shouldSkipRescan = (lastScan, code, now, cooldownMs = SAME_CODE_COOLDOWN_MS) => {
  if (!lastScan || lastScan.code !== code) return false;
  return now - lastScan.at < cooldownMs;
};
